import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Heart, Handshake, ArrowRight } from 'lucide-react';
import { partners } from '../data/mock';
import Newsletter from '../components/Newsletter';

const Partners = () => {
  const sponsors = partners.filter(p => p.type === 'sponsor');
  const communityPartners = partners.filter(p => p.type === 'community');

  return (
    <div className="bg-stone-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50 to-stone-50" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-full text-teal-600 text-sm font-medium mb-6 shadow-sm">
              <Handshake size={16} />
              <span>Our Supporters</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-800 mb-6">
              Sponsors & <span className="text-teal-600">Partners</span>
            </h1>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              We are grateful for the support of organizations and businesses that stand 
              with us in the fight for civil and human rights and justice.
            </p>
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      {sponsors.length > 0 && (
        <section className="py-16 relative bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
                Our <span className="text-teal-600">Sponsors</span>
              </h2>
              <p className="text-lg text-stone-600">
                Organizations that support our mission for civil and human rights and justice
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sponsors.map((partner) => (
                <a
                  key={partner.id}
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div className="p-8 bg-stone-50 border border-stone-200 rounded-xl hover:border-teal-500/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-lg h-full">
                    {/* Logo Placeholder */}
                    <div className="w-full h-32 bg-white border border-stone-200 rounded-xl flex items-center justify-center mb-6 group-hover:border-teal-200 transition-all duration-300">
                      <span className="text-2xl font-bold text-teal-600 text-center px-4">
                        {partner.name}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-semibold text-stone-800 mb-3 group-hover:text-teal-600 transition-colors">
                      {partner.name}
                    </h3>
                    
                    <p className="text-stone-600 mb-4 leading-relaxed">
                      {partner.description}
                    </p>
                    
                    <div className="flex items-center gap-2 text-teal-600 group-hover:text-teal-700 transition-colors">
                      <span className="font-medium">Visit Website</span>
                      <ExternalLink size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Community Partners Section */}
      {communityPartners.length > 0 && (
        <section className="py-16 relative bg-amber-50">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
                Community <span className="text-teal-600">Partners</span>
              </h2>
              <p className="text-lg text-stone-600">
                Organizations and collectives working alongside us to strengthen our community
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {communityPartners.map((partner) => (
                <a
                  key={partner.id}
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div className="p-8 bg-white border border-stone-200 rounded-2xl hover:border-teal-500/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-lg h-full">
                    {/* Logo Placeholder */}
                    <div className="w-full h-32 bg-gradient-to-br from-stone-50 to-amber-50 rounded-xl flex items-center justify-center mb-6 group-hover:to-teal-50 transition-all duration-300 border border-stone-200">
                      <span className="text-2xl font-bold text-teal-600 text-center px-4">
                        {partner.name}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-semibold text-stone-800 mb-3 group-hover:text-teal-600 transition-colors">
                      {partner.name}
                    </h3>
                    
                    <p className="text-stone-600 mb-4 leading-relaxed">
                      {partner.description}
                    </p>
                    
                    <div className="flex items-center gap-2 text-teal-600 group-hover:text-teal-700 transition-colors">
                      <span className="font-medium">Learn More</span>
                      <ExternalLink size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Become a Partner CTA */}
      <section className="py-24 relative bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-12 bg-gradient-to-br from-teal-50 to-amber-50 border border-stone-200 rounded-2xl text-center shadow-sm">
            <div className="w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Heart className="text-white" size={32} />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
              Become a <span className="text-teal-600">Partner</span>
            </h2>
            
            <p className="text-lg text-stone-600 mb-8 max-w-2xl mx-auto">
              Interested in supporting our mission? Whether you're a business, organization, 
              or individual looking to make a difference, we'd like to hear from you.
            </p>
            
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-10 py-5 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-lg rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-teal-600/25"
            >
              Get in Touch
              <ArrowRight size={22} />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
};

export default Partners;
